/**
 * Created by @author @ddennis - ddennis.dk aka fantastisk.dk/works aka meresukker.dk on 27/07/2025.
 */
import { Link, useParams } from 'react-router-dom'
import {
  COMPLETE_STUDY,
  COMPLETED,
  DATA_SYNCED,
  ELIGIBILITY_ERROR,
  NEEDS_DATA_SYNC,
  NO_ELIGIBILITY,
  SUBJECT_IS_TERMINATED,
  SUBJECT_JOURNEY,
  usePhaseConfigEvaluator,
} from '../../../hooks/usePhaseConfigEvaluator'

import { EligibilityMoveView } from './EligibilityMoveView'
import { InitiateDataSync } from './InitiateDataSync'
import { NoEligibilityView } from './NoEligibilityView'
import { SubjectJourney } from './SubjectJourney'
import { DrawerContainer } from '../../../components/drawer/DrawerContainer'
import React, { Suspense, useEffect, useState } from 'react'
import { Button } from 'antd'
import { COLOR } from '../../../constants'
import { RightOutlined } from '@ant-design/icons'
import { SUBJECT_STATUS_ENUM, SubjectType } from '../../../Types'
import { useProtocol } from '../../../hooks/useProtocol'
import { EligiblityCalculationError } from '../eligibility/EligiblityCalculationError'
import { getSubjectJourney } from '../../../util/subject-journey'
import Spinner from '../../../components/spinner/Spinner'
import { CompleteStudyDrawer } from '../subject_update/CompleteStudyDrawer'
import { DEVICE_UNAVAILBLE_ROUTE, UNCOMPLETE_ROUTE } from '../../../routes'

type MoveDrawerProps = {
  subjectData: any
  onClose?: () => void
}

export const VIEW_TYPE = {
  SUBJECT_JOURNEY: SUBJECT_JOURNEY,
  MOVE_SUBJECT: 'MOVE_SUBJECT',
} as const

const sizeObj = {
  SUBJECT_JOURNEY: 400,
  NEEDS_DATA_SYNC: 550,
  SUBJECT_IS_TERMINATED: 300,
  DATA_SYNCED: '70%',
  NO_ELIGIBILITY: 550,
  ELIGIBILITY_ERROR: 500,
} as const

type StateType = keyof typeof VIEW_TYPE

export const MoveDrawer = ({ subjectData, onClose }: MoveDrawerProps) => {
  const { projectId } = useParams() as { projectId: string }

  const { data: protocolData } = useProtocol(projectId)

  const [drawerOpen, setDrawerOpen] = useState(true)
  const [navigateToPath, setNavigateToPath] = useState<string>('../')

  const [expandedSize, setExpandedSize] = useState<number | string | null>(null)

  const [state, setState] = useState<StateType>(VIEW_TYPE.SUBJECT_JOURNEY)

  //
  // this view can render
  // - needs data sync
  // - data sync in progress
  //
  // - eligibility calculation error
  // - eligibility calculation
  //
  // - Regular move subject - with body part
  // - Regular move subject - without body part
  //
  // - Terminate subject
  // - Reactivate subject
  //
  //

  const drawerWidth = expandedSize ? expandedSize : 360

  const navigateToTerminate = () => {
    setNavigateToPath('../terminate')
    setDrawerOpen(false)
  }

  const navigateToReactivate = () => {
    setNavigateToPath('../reactivate')
    setDrawerOpen(false)
  }

  const navigateToMoveBack = () => {
    setNavigateToPath('../move-back')
    setDrawerOpen(false)
  }

  const navigateToUnComplete = () => {
    setNavigateToPath('../' + UNCOMPLETE_ROUTE.to)
    setDrawerOpen(false)
  }

  const navigateToDeviceNotAvailble = () => {
    setNavigateToPath('../' + DEVICE_UNAVAILBLE_ROUTE.to)
    setDrawerOpen(false)
  }

  const closeDrawer = () => {
    setDrawerOpen(false)
  }

  const setDrawerSize = (size: number | string) => {
    setExpandedSize(size)
  }

  return (
    <DrawerContainer
      open={drawerOpen}
      projectId={projectId}
      setVisible={onClose}
      width={drawerWidth}
      to={navigateToPath}
      header={null}
      bodyStyle={{ padding: '0px 0px 0px 0px' }}
    >
      <div className="container-fluid h-100 d-flex flex-column ">
        {state === VIEW_TYPE.MOVE_SUBJECT ? (
          <GetComponent
            subjectData={subjectData}
            navigateToTerminate={navigateToTerminate}
            closeDrawer={closeDrawer}
            setDrawerSize={setDrawerSize}
            navigateToDeviceNotAvailble={navigateToDeviceNotAvailble}
          ></GetComponent>
        ) : null}

        {state === VIEW_TYPE.SUBJECT_JOURNEY ? (
          <div className="container flex-grow-1 d-flex flex-column px-0">
            <UpdateButtons
              subjectData={subjectData}
              phasesData={protocolData.phases}
              closeDrawer={closeDrawer}
              setState={setState}
              navigateToReactivate={navigateToReactivate}
              navigateToMoveBack={navigateToMoveBack}
              navigateToUnComplete={navigateToUnComplete}
              navigateToTerminate={navigateToTerminate}
            ></UpdateButtons>
          </div>
        ) : null}
      </div>
    </DrawerContainer>
  )
}

const GetComponent = ({
  navigateToTerminate,
  closeDrawer,
  subjectData,
  setDrawerSize,
  navigateToDeviceNotAvailble,
}) => {
  const { subjectId, projectId } = useParams() as { subjectId: string; projectId: string }
  const { data, mutate } = usePhaseConfigEvaluator(projectId, subjectId)

  const type = data?.type

  useEffect(() => {
    const startDrawerWidth = data?.type ? sizeObj[data.type] : null
    setDrawerSize(startDrawerWidth)
  }, [type, setDrawerSize, data?.type])

  switch (type) {
    case SUBJECT_IS_TERMINATED:
      return (
        <div className="row flex-grow-1 d-flex ">
          <div className="col-12 mx-auto">
            <h3>Subject is terminated</h3>
            <Link to="../reactivate">
              <Button type="primary">Reactivate subject</Button>
            </Link>
          </div>
        </div>
      )

    case NEEDS_DATA_SYNC:
    case DATA_SYNCED:
      return (
        <div className="row flex-grow-1 d-flex ">
          {type === NEEDS_DATA_SYNC ? (
            <div className="col-12 mx-auto p-4 ">
              <InitiateDataSync outerMutate={mutate} navigateToDeviceNotAvailble={navigateToDeviceNotAvailble} />
            </div>
          ) : (
            <Suspense fallback={<Spinner></Spinner>}>
              <EligibilityMoveView navigateToTerminate={navigateToTerminate} closeDrawer={closeDrawer} />
            </Suspense>
          )}
        </div>
      )

    case NO_ELIGIBILITY:
      return (
        <div className="row p-4 ">
          <NoEligibilityView
            onClose={closeDrawer}
            navigateToTerminate={navigateToTerminate}
            subjectData={subjectData}
            setDrawerSize={setDrawerSize}
          />
        </div>
      )

    case COMPLETE_STUDY:
      return (
        <div className="row p-4 ">
          <CompleteStudyDrawer closeDrawer={closeDrawer}></CompleteStudyDrawer>
        </div>
      )

    case COMPLETED:
      return <h1>sdfsdf</h1>

    case ELIGIBILITY_ERROR:
      return (
        <div className="row p-4">
          <EligiblityCalculationError onClose={closeDrawer} eligibilityError={data.payload.eligibility.message} />
        </div>
      )

    default:
      return null
  }
}

type UpdateButtonsProps = {
  setState: (value: any) => void
  subjectData: SubjectType
  phasesData: any
  closeDrawer: () => void
  navigateToReactivate: () => void
  navigateToMoveBack: () => void
  navigateToUnComplete: () => void
  navigateToTerminate: () => void
}
const UpdateButtons = ({
  subjectData,
  phasesData,
  closeDrawer,
  setState,
  navigateToReactivate,
  navigateToMoveBack,
  navigateToUnComplete,
  navigateToTerminate,
}: UpdateButtonsProps) => {
  const handleMoveNextPhase = () => {
    setState(VIEW_TYPE.MOVE_SUBJECT)
  }

  const journeyObj = getSubjectJourney(phasesData, subjectData)

  const moveForwardContent = (
    <p>
      <span style={{ opacity: 0.92 }}>Move to</span> <span className="fw-bold">Next phase</span>
    </p>
  )

  const moveToCompletion = (
    <p>
      <span style={{ opacity: 0.92 }}>Move to</span> <span className="fw-bold">Study Completion</span>
    </p>
  )

  return (
    <div className="row py-4 px-2">
      <div className="col-12 px-5 mb-2 mt-1">
        <SubjectJourney subjectData={subjectData} phasesData={phasesData}></SubjectJourney>
      </div>

      <div className="col-12">
        <div className="bg-black w-100 mb-3" style={{ height: 1, opacity: 0.2 }}></div>
      </div>

      <div className="col-12 ">
        {/*   IF subject is terminateed   */}

        {subjectData.status === SUBJECT_STATUS_ENUM.TERMINATED ? (
          <div className="px-3 py-4 mb-3 border rounded-2" style={{ background: COLOR.LIGHT_GRAY }}>
            <h6 className="fw-bold">Participant has been terminated</h6>
            <p className="mb-3 opacity-75">
              Reactivate this participant and resume ePRO data collection. The subject will return to their previous
              phase.
            </p>
            <Button type="default" onClick={navigateToReactivate} className="me-2 w-100" size="large">
              Reactivate participant
            </Button>
          </div>
        ) : null}

        {/*   MOVE SUBJECT BUTTONS   */}
        {subjectData.status === SUBJECT_STATUS_ENUM.ACTIVE ? (
          <>
            <Button
              type="primary"
              onClick={handleMoveNextPhase}
              className="w-100 "
              size="large"
              style={{ boxShadow: '0px 3px 8px -3px rgba(0, 0, 0, 0.3)' }}
            >
              <div className="d-flex justify-content-between align-items-center">
                {journeyObj.nextPhase ? moveForwardContent : moveToCompletion}
                <RightOutlined />
              </div>
            </Button>

            <TerminateSubjectButton subjectData={subjectData} navigateToTerminate={navigateToTerminate} />

            {journeyObj.previousPhase ? (
              <div className="mt-2 pointer">
                <Button onClick={navigateToMoveBack} type="text" className="px-1">
                  <p className="p-small">
                    <span className="me-1 text-black opacity-50">Made a mistake:</span>
                    <span className="opacity-75 text-decoration-underline" style={{ color: '#004488' }}>
                      Move Participant to Previous Phase
                    </span>
                  </p>
                </Button>
              </div>
            ) : null}
          </>
        ) : null}

        {subjectData.status === SUBJECT_STATUS_ENUM.COMPLETED ? (
          <div className="px-3 py-4 mb-3 border rounded-2" style={{ background: COLOR.LIGHT_GRAY }}>
            <h6 className="fw-normal">
              <span className="">Participant has been</span> <span className="fw-bold">Completed</span>
            </h6>
            <p className="mb-3 opacity-75">Made a mistake? Click below to undo the study completion.</p>
            <Button
              type="default"
              onClick={navigateToUnComplete}
              className="w-100 "
              size="large"
              style={{ boxShadow: '0px 3px 8px -3px rgba(0, 0, 0, 0.15)' }}
            >
              {/*<div className="d-flex justify-content-between align-items-center">
                <p>
                  <span style={{ opacity: 0.92 }}>Revert </span> <span className="fw-bold">Study Completion</span>
                </p>
                <RightOutlined />
              </div>*/}
              <p className=" ">Revert Study Completion</p>
            </Button>
          </div>
        ) : null}
      </div>
    </div>
  )
}

const TerminateSubjectButton = ({ subjectData, navigateToTerminate }) => {
  const terminateSubjectButton =
    subjectData.status === SUBJECT_STATUS_ENUM.TERMINATED ||
    subjectData.status === SUBJECT_STATUS_ENUM.COMPLETED ? null : (
      <Button onClick={navigateToTerminate} className="me-2 w-100 mt-3" size="large">
        Terminate participant
      </Button>
    )

  return terminateSubjectButton
}
